import { CDBTable, CDBTableBody, CDBTableHeader, CDBSpinner } from "cdbreact";
import { useEffect, useMemo, useRef, useState } from "react";
import { CDBBtn } from "cdbreact";
import Swal from "sweetalert2";
import TrimmedText from "../../utils/TrimmedText";
import styles from "./Table.module.css";

const SKU_FILTER_KEYS = {
  "Código SAP": "CodeSap",
  "Descripción": "SkuDescription",
  "Código Proveedor": "Nit",
  "Proveedor": "ProjectName",
  "Tipo de Artículo": "article_type",
  "Categoría": "category",
  "Rop": "Rop",
  "Cant. Inventario": "Inventory",
  "Unid. de medida": "unit_type",
  "% Inventario": "PercentInventory",
  "Cant. a enviar": "SendQuantity",
  "MTS/MTO": "show_status",
};

const ExcelColumnFilter = ({
  columnKey,
  activeValues = [],
  activeSearch = "",
  loadOptions,
  onApply,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const hasFilter =
    activeValues.length > 0 ||
    (activeSearch && activeSearch.trim() !== "");

  const visibleOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((value) => {
      const label = value === "" ? "(vacíos)" : String(value);
      return label.toLowerCase().includes(term);
    });
  }, [options, search]);

  const allVisibleSelected =
    visibleOptions.length > 0 && visibleOptions.every((value) => draft.includes(value));

  const openFilter = async (event) => {
    event.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }

    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const popupWidth = 300;
      const desiredLeft = rect.right - popupWidth;
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(desiredLeft, window.innerWidth - popupWidth - 8)),
      });
    }

    setOpen(true);
    setSearch(activeSearch || "");
    setLoading(true);
    const fetched = await loadOptions(columnKey);
    const unique = [...new Set([...(fetched || []).map(String), ...activeValues.map(String)])];
    setOptions(unique);
    setDraft(activeValues.length > 0 ? activeValues.map(String) : unique);
    setLoading(false);
  };

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const toggleValue = (value) => {
    setDraft((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const toggleAllVisible = () => {
    setDraft((current) => {
      if (allVisibleSelected) {
        return current.filter((value) => !visibleOptions.includes(value));
      }
      return [...new Set([...current, ...visibleOptions])];
    });
  };

  const applyFilter = () => {
    const searchTerm = search.trim();

    // Si hay texto en Buscar, aplicar una búsqueda por contenido
    // sobre toda la columna en el backend (LIKE %texto%).
    if (searchTerm !== "") {
      onApply(columnKey, [], searchTerm);
      setOpen(false);
      return;
    }

    // Sin texto de búsqueda se conserva el comportamiento por checks.
    if (draft.length === 0) return;

    // Si todos los valores disponibles están seleccionados, equivale a no filtrar.
    const selected =
      options.length > 0 && options.every((value) => draft.includes(value))
        ? []
        : draft;

    onApply(columnKey, selected, "");
    setOpen(false);
  };

  const clearFilter = () => {
    setSearch("");
    setDraft([]);
    onApply(columnKey, [], "");
    setOpen(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={hasFilter ? "Filtro aplicado" : "Filtrar como Excel"}
        onClick={openFilter}
        style={{
          marginLeft: 6,
          minWidth: 24,
          height: 22,
          padding: "0 5px",
          border: hasFilter ? "1px solid #0b5f82" : "1px solid #146284",
          borderRadius: 3,
          background: hasFilter ? "#146284" : "#ffffff",
          color: hasFilter ? "#ffffff" : "#146284",
          cursor: "pointer",
          fontSize: 13,
          fontWeight: 700,
          lineHeight: "20px",
          verticalAlign: "middle",
          boxShadow: "0 1px 2px rgba(0,0,0,.12)",
        }}
      >
        {hasFilter ? "▼•" : "▼"}
      </button>

      {open && (
        <div
          ref={popupRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            width: 300,
            maxHeight: 430,
            background: "#fff",
            border: "1px solid #b7b7b7",
            boxShadow: "0 8px 24px rgba(0,0,0,.22)",
            zIndex: 99999,
            color: "#222",
            textAlign: "left",
            fontWeight: 400,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 10, borderBottom: "1px solid #ddd", background: "#fafafa" }}>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar"
              style={{
                width: "100%",
                height: 32,
                padding: "5px 8px",
                border: "1px solid #aaa",
                borderRadius: 2,
                fontSize: 13,
              }}
            />
          </div>

          <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleAllVisible}
              />
              <strong>(Seleccionar todo)</strong>
            </label>
          </div>

          <div style={{ maxHeight: 260, overflowY: "auto", padding: "4px 10px" }}>
            {loading ? (
              <div style={{ padding: 16, textAlign: "center", fontSize: 13 }}>Cargando valores...</div>
            ) : visibleOptions.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "#666", fontSize: 13 }}>
                No se encontraron valores
              </div>
            ) : (
              visibleOptions.map((value, index) => (
                <label
                  key={`${value}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    margin: 0,
                    padding: "5px 0",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 400,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={draft.includes(value)}
                    onChange={() => toggleValue(value)}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {value === "" ? "(Vacíos)" : value}
                  </span>
                </label>
              ))
            )}
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              padding: 10,
              borderTop: "1px solid #ddd",
              background: "#fafafa",
            }}
          >
            <button
              type="button"
              onClick={clearFilter}
              disabled={!hasFilter}
              style={{
                border: "1px solid #aaa",
                background: "#fff",
                borderRadius: 2,
                padding: "6px 12px",
                cursor: hasFilter ? "pointer" : "default",
                opacity: hasFilter ? 1 : 0.5,
              }}
            >
              Limpiar
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ border: "1px solid #aaa", background: "#fff", borderRadius: 2, padding: "6px 12px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={applyFilter}
                disabled={(draft.length === 0 && search.trim() === "") || loading}
                style={{
                  border: "1px solid #0b5f82",
                  background: "#146284",
                  color: "#fff",
                  borderRadius: 2,
                  padding: "6px 14px",
                  cursor:
                    (draft.length > 0 || search.trim() !== "") && !loading
                      ? "pointer"
                      : "default",
                  opacity:
                    (draft.length > 0 || search.trim() !== "") && !loading
                      ? 1
                      : 0.55,
                }}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Table = ({
  headers,
  loading,
  data,
  setPressedButton,
  changeIcon,
  stateEdit,
  stateSkus,
  statePurchases,
  cState,
  providerState,
  stateDispatch,
  colorPorcentage,
  excelColumnFilters,
  setExcelColumnFilters,
  excelColumnSearchFilters,
  setExcelColumnSearchFilters,
  getExcelFilterOptions,
}) => {
  const [dataState, setDataState] = useState([]);

  useEffect(() => {
    setDataState(data);
  }, [data]);

  const excelFiltersEnabled =
    stateSkus &&
    excelColumnFilters !== undefined &&
    typeof setExcelColumnFilters === "function" &&
    typeof getExcelFilterOptions === "function";

  const applyExcelFilter = (columnKey, values, searchTerm = "") => {
    // Valores exactos seleccionados mediante los checks.
    setExcelColumnFilters((current) => {
      const next = { ...(current || {}) };
      if (!values || values.length === 0) {
        delete next[columnKey];
      } else {
        next[columnKey] = values;
      }
      return next;
    });

    // Texto escrito en Buscar: el backend lo interpreta como CONTIENE.
    if (typeof setExcelColumnSearchFilters === "function") {
      setExcelColumnSearchFilters((current) => {
        const next = { ...(current || {}) };
        const text = (searchTerm || "").trim();

        if (!text) {
          delete next[columnKey];
        } else {
          next[columnKey] = text;
        }

        return next;
      });
    }
  };

  const getRowColor = (row) => {
    const percentInventory = parseFloat(row);

    if (!isNaN(percentInventory) && colorPorcentage) {
      if (percentInventory < 10) return "#000";
      if (percentInventory >= 10 && percentInventory < 30) return "#E53935";
      if (percentInventory >= 30 && percentInventory < 70) return "#ffd600";
      if (percentInventory >= 70 && percentInventory <= 100) return "#2e7d32";
      return "#1565c0";
    } else if (!isNaN(percentInventory) && !colorPorcentage) {
      if (percentInventory < 65) return "#E53935";
      if (percentInventory >= 65 && percentInventory < 75) return "#ffd600";
      if (percentInventory >= 75 && percentInventory < 105) return "#2e7d32";
      return "#1565c0";
    }

    return "#000000";
  };

  const formatCellValue = (cell) => cell;

  if (loading) {
    return (
      <div className="d-flex m-4 justify-content-center align-items-center">
        <CDBSpinner multicolor />
      </div>
    );
  }

  if (data.length === 0 && !excelFiltersEnabled) {
    return (
      <div className={styles.noData}>
        <h1>Ningún elemento encontrado</h1>
        <p className={styles.noDataDesc}>Intenta refrescar</p>
      </div>
    );
  }

  return (
    <CDBTable responsive hover stripped>
      <CDBTableHeader>
        <tr>
          {headers.map((header, index) => {
            const isUnidadesRecibidas = header === "Unidades recibidas";
            const columnKey = SKU_FILTER_KEYS[header];

            return (
              <th key={index} className={styles.theadDark} style={{ whiteSpace: "nowrap" }}>
                <span>{header}</span>
                {excelFiltersEnabled && columnKey && (
                  <ExcelColumnFilter
                    columnKey={columnKey}
                    activeValues={excelColumnFilters?.[columnKey] || []}
                    activeSearch={excelColumnSearchFilters?.[columnKey] || ""}
                    loadOptions={getExcelFilterOptions}
                    onApply={applyExcelFilter}
                  />
                )}
                {isUnidadesRecibidas && (
                  <i
                    className="fas fa-info-circle"
                    onClick={() => {
                      Swal.fire({
                        title: "Información",
                        text: "El cálculo de las unidades recibidas se realiza tomando las unidades recibidas y restando las unidades defectuosas de la Recepción.",
                        icon: "info",
                        confirmButtonText: "Aceptar",
                        customClass: { confirmButton: "btn btn-primary" },
                      });
                    }}
                    onMouseOver={() => { document.body.style.cursor = "pointer"; }}
                    onMouseOut={() => { document.body.style.cursor = "default"; }}
                    style={{ marginLeft: "5px" }}
                  />
                )}
              </th>
            );
          })}

          {(cState || providerState) && (
            <th style={{ textAlign: "center" }} className={styles.theadDark}>
              Acciones
            </th>
          )}
        </tr>
      </CDBTableHeader>
      <CDBTableBody>
        {dataState && dataState.length > 0 ? (
          dataState.map((row, index) => {
            let textColor;

            function contienePorcen(elemento) {
              if (typeof elemento === "string" || typeof elemento === "number") {
                const strElemento = String(elemento);
                if (strElemento.includes("%")) textColor = getRowColor(elemento);
                return strElemento.includes("%");
              }
              return false;
            }

            return (
              <tr key={index}>
                {Object.entries(row).map(([key, cell], cellIndex) => (
                  <td
                    key={cellIndex}
                    style={{
                      fontWeight: contienePorcen(row[key]) ? "bold" : "normal",
                      color: contienePorcen(row[key]) ? textColor : "inherit",
                    }}
                  >
                    <TrimmedText text={formatCellValue(cell)} size={50} />
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <div className="d-flex align-items-center justify-content-center small-filter">
                    {changeIcon && (
                      <CDBBtn
                        title="Reporte Proveedor"
                        size="large"
                        className="p-2 btn"
                        style={{ marginRight: "2px" }}
                        onClick={() => setPressedButton("individualMonthlyReport," + row[0])}
                      >
                        <i className="far fa-file-alt"></i>
                      </CDBBtn>
                    )}
                    {stateDispatch && (
                      <CDBBtn
                        title="Cargar Skus"
                        size="large"
                        className="p-2 btn"
                        style={{ marginLeft: "2px" }}
                        onClick={() => setPressedButton("stateDispatch," + row[0] + "/*-/" + row[1])}
                      >
                        <i className="fas fa-dolly-flatbed"></i>
                      </CDBBtn>
                    )}
                    {(cState || providerState) && (
                      <CDBBtn
                        title={changeIcon ? "Ver SKUS" : "Editar"}
                        size="large"
                        className="p-2 btn"
                        onClick={() => {
                          if (stateEdit) {
                            setPressedButton("edit," + row[0] + "/*-/" + row[3]);
                          } else if (stateDispatch) {
                            setPressedButton("edit," + row[0] + "/*-/" + row[1]);
                          } else if (stateSkus) {
                            setPressedButton("edit," + row[0] + "/*-/" + row[2]);
                          } else {
                            setPressedButton("edit," + (row.CodeSap ?? row[0]));
                          }
                        }}
                      >
                        {changeIcon ? <i className="fas fa-chart-bar"></i> : <i className="fas fa-pencil-alt"></i>}
                      </CDBBtn>
                    )}
                    {stateSkus && (
                      <CDBBtn
                        title="Ver indicadores"
                        size="large"
                        className="p-2 btn"
                        style={{ marginLeft: "2px" }}
                        onClick={() => setPressedButton("indicators," + (row.CodeSap ?? row[0]))}
                      >
                        <i className="fas fa-chart-line"></i>
                      </CDBBtn>
                    )}
                    {statePurchases && (
                      <CDBBtn
                        title="Ver orden de compra"
                        size="large"
                        className="p-2 btn"
                        style={{ marginLeft: "2px" }}
                        onClick={() => setPressedButton("viewBill," + (row.CodeSap ?? row[0]))}
                      >
                        <i className="fas fa-eye"></i>
                      </CDBBtn>
                    )}
                    {statePurchases && (
                      <CDBBtn
                        title="Descargar orden de compra"
                        size="large"
                        className="p-2 btn"
                        style={{ marginLeft: "2px" }}
                        onClick={() => setPressedButton("downloadBill," + (row.CodeSap ?? row[0]))}
                      >
                        <i className="fas fa-file-download"></i>
                      </CDBBtn>
                    )}
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={headers.length + ((cState || providerState) ? 1 : 0)} style={{ textAlign: "center", padding: 28 }}>
              Ningún elemento encontrado. Puedes abrir un filtro del encabezado para modificarlo o limpiarlo.
            </td>
          </tr>
        )}
      </CDBTableBody>
    </CDBTable>
  );
};

export default Table;
